import { useFormContext } from 'react-hook-form'
import { TextField } from '@boltenergy-be/design-system'
import { InputProps } from '../types'
import { preventSpecialCharactersEvent } from 'utils/eventHandlers'
import IBAN from 'iban'
import { removeNonAlphanumericals } from 'utils/format.ts'
import { useTranslation } from 'react-i18next'
import { getFieldError, getFieldName } from 'components/form-fields/utils.ts'
import { ALLOWED_IBAN_COUNTRIES } from '@boltenergy-be/shared'

const Iban = ({ description, required, label, identifier = 'iban', parentDotPath }: InputProps) => {
  // Constants
  const fieldName = getFieldName(identifier, parentDotPath)

  // React Hook Form
  const {
    formState: { errors },
    register,
    setValue
  } = useFormContext()

  // i18n
  const { t } = useTranslation(['commonFormFields', 'validation'])

  return (
    <TextField
      id={fieldName}
      {...register(fieldName, {
        required: {
          value: !!required,
          message: t(($) => $.required, {
            ns: 'validation'
          })
        },
        validate: {
          allowed: (value) =>
            (!required && !value) ||
            ALLOWED_IBAN_COUNTRIES.some((allowed) => value.slice(0, 2) === allowed) ||
            t(($) => $.invalid.ibanCountries, {
              ns: 'validation'
            }),
          valid: (value) =>
            (!required && !value) ||
            IBAN.isValid(value) ||
            t(($) => $.invalid.iban, {
              ns: 'validation'
            })
        }
      })}
      error={getFieldError(identifier, parentDotPath, errors)?.message}
      grid="full"
      label={
        label ||
        t(($) => $.iban, {
          ns: 'commonFormFields'
        })
      }
      description={description}
      onKeyDown={(e) => preventSpecialCharactersEvent(e)}
      onPaste={(e) => {
        // Format the paste value
        const formattedPasteValue = removeNonAlphanumericals(e.clipboardData.getData('text'))

        // Set the formatted value to the input & the form
        e.currentTarget.value = formattedPasteValue
        setValue('iban', formattedPasteValue)

        // Prevent the default paste behavior
        e.preventDefault()
      }}
      {...(!required && { optionalText: t(($) => $.optional) })}
      type="text"
    />
  )
}

export default Iban
